import { Component, inject, computed, ElementRef, ViewChild, effect, signal, HostListener, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { TooltipService } from '../../../shared/services/tooltip.service';
import { PlatformService } from '../../../shared/services/platform.service';
import { calculateTooltipPosition, type VerticalSide } from '../../../shared/utils/tooltip-position.utils';

/**
 * HoverComponent - Global tooltip managed by TooltipService
 * 
 * Place once at app root level. Control via TooltipService.
 * 
 * Smart positioning:
 *   - Default: above the trigger, centered
 *   - Near top edge: flips below
 *   - Near right edge: aligns to right, arrow shifts
 *   - Near left edge: aligns to left, arrow shifts
 *   - Near bottom edge (when flipped below): stays above
 * 
 * Usage in any component:
 * 
 * constructor(private tooltipService: TooltipService) {}
 * 
 * <button 
 *   (mouseenter)="tooltipService.show($event, 'Tooltip text')"
 *   (mouseleave)="tooltipService.hide()">
 *   Hover me
 * </button>
 */

@Component({
  selector: 'app-hover-component',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './hover-component.html',
  styleUrl: './hover-component.scss'
})
export class HoverComponent implements OnDestroy {
  private platform = inject(PlatformService);
  private tooltipService = inject(TooltipService);
  private ngZone = inject(NgZone);
  private scrollListener?: () => void;
  
  @ViewChild('tooltipElement', { read: ElementRef }) tooltipElement?: ElementRef<HTMLDivElement>;
  
  tooltip = this.tooltipService.tooltip;
  
  visible = computed(() => this.tooltip().visible);
  text = computed(() => this.tooltip().text);
  icon = computed(() => this.tooltip().icon);

  /** Resolved position written after layout measurement */
  resolvedStyle = signal<Record<string, string | number>>({
    'left.px': 0,
    'top.px': 0,
    '--arrow-offset': '0px'
  });

  /** Which side the tooltip opened on (drives arrow direction via CSS class) */
  verticalSide = signal<VerticalSide>('above');

  private readonly GAP = 8; // distance between trigger and tooltip

  constructor() {
    effect(() => {
      if (this.visible()) {
        // Wait one frame so the tooltip element is rendered & measurable
        requestAnimationFrame(() => this.reposition());
        this.addScrollListener();
      } else {
        this.removeScrollListener();
      }
    });
  }

  ngOnDestroy(): void {
    this.removeScrollListener();
  }

  /** Hide tooltip when clicking anywhere outside */
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.visible()) {
      this.tooltipService.hide();
    }
  }

  /** Hide tooltip on Ionic page transitions */
  @HostListener('document:ionRouteDidChange')
  onNavigation(): void {
    if (this.visible()) {
      this.tooltipService.hide();
    }
  }

  /** Hide tooltip on Escape key */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible()) {
      this.tooltipService.hide();
    }
  }

  private addScrollListener(): void {
    this.removeScrollListener();
    const handler = () => this.ngZone.run(() => this.tooltipService.hide());
    document.addEventListener('scroll', handler, { capture: true, passive: true });
    this.scrollListener = () => document.removeEventListener('scroll', handler, true);
  }

  private removeScrollListener(): void {
    this.scrollListener?.();
    this.scrollListener = undefined;
  }

  // ── Core repositioning logic ────────────────────────────────
  private reposition(): void {
    const el = this.tooltipElement?.nativeElement;
    if (!el || !this.platform.isBrowser) return;

    const state = this.tooltip();
    const anchorRect = state.anchorRect;

    const anchorCx = anchorRect ? anchorRect.left + anchorRect.width / 2 : state.x;
    const anchorTop = anchorRect ? anchorRect.top : state.y;
    const anchorBottom = anchorRect ? anchorRect.bottom : state.y + 24;

    const pos = calculateTooltipPosition({
      anchor: { cx: anchorCx, top: anchorTop, bottom: anchorBottom },
      tooltipWidth: el.offsetWidth,
      tooltipHeight: el.offsetHeight,
      containerWidth: this.platform.innerWidth,
      containerHeight: window.innerHeight,
      gap: this.GAP,
    });

    this.verticalSide.set(pos.side);
    this.resolvedStyle.set({
      'left.px': pos.left,
      'top.px': pos.top,
      '--arrow-offset': `${pos.arrowOffset}px`
    });
  }

  onIconClick(event: Event): void {
    event.stopPropagation();
    const text = this.tooltipService.getCurrentText();
    console.log('ℹ️ Tooltip icon clicked:', text);
  }
}
